import { useState } from "react";
import { Link } from "react-router-dom";

import {
  type OptionItem,
  OptionType,
  Status,
  useGetOptionsQuery,
} from "@/api/admin";
import { type IBranchInfo, useGetAllBranchesQuery } from "@/api/admin/branches";
import { AdminSections } from "@/api/admin/teamRoles";
import {
  Loader,
  TableCellBadge,
  TableCellLink,
  TableCellText,
  TableHeadCellCustom,
  type TableHeaderType,
} from "@/components/atoms";
import {
  DropDownViewMenu,
  FilterSearch,
  type StatusVariants,
  type SubMenuConfig,
} from "@/components/molecules";
import type { SubMenuItem } from "@/components/molecules/FilterSearch/DropDownFilterMenu";
import { PATHS } from "@/config/path";
import { usePermission } from "@/services/casl/usePermission";
import { formatIsoDate } from "@/utils/formatIsoToUTC";
import { useParamsFromUrl } from "@/utils/hooks/useParamsFromUrl";
import { useTableParams } from "@/utils/hooks/useTableParams";
import { useVisibleFlags } from "@/utils/hooks/useVisibleFlags";

import DynamicTable from "../DynamicTable";
import { type ColumnConfig, isOptionItem } from "../type";

enum AccountBranchesColumn {
  BRANCH_NAME = "Branch name",
  REFERENCE_NUMBER = "Reference number",
  DATE_TIME = "Created at",
  LEGAL_ENTITY = "Legal entity",
  PHONE = "Phone",
  ACTIVITY = "Status",
  ADDRESS = "Address",
}

const TABLE_HEADERS: TableHeaderType = Object.values(AccountBranchesColumn);

const AccountBranchesTable = () => {
  const hasPermission = usePermission(AdminSections.BRANCH);

  const [searchValue, setSearchValue] = useState("");

  const { tableParams, handleNext, handlePrev, setTableParams } =
    useTableParams(
      {
        initialParams: { query: "" },
      },
      searchValue
    );

  useParamsFromUrl(setTableParams, ["legalEntityId", "brandId"]);

  const { data: branchData, isPending: isBranchPending } =
    useGetAllBranchesQuery(tableParams);

  const { visibleColumns, setVisibleColumns, flags } =
    useVisibleFlags(TABLE_HEADERS);

  const { data: optionsData, isPending: isOptionPending } = useGetOptionsQuery([
    OptionType.LegalEntities,
  ]);

  const handleSetFilter = (
    selectedMap: Record<string, string[] | OptionItem[]>
  ) => {
    const selectedActivity =
      selectedMap[AccountBranchesColumn.ACTIVITY]?.[0] ?? "";
    const rawLegal = selectedMap[AccountBranchesColumn.LEGAL_ENTITY]?.[0] ?? "";

    setTableParams(prev => ({
      ...prev,
      active: selectedActivity
        ? selectedActivity === Status.INACTIVE
          ? false
          : true
        : undefined,
      legalEntityId: isOptionItem(rawLegal) ? rawLegal.id : undefined,
      offset: 0,
    }));
  };

  const SUB_MENUS: Record<string, SubMenuItem[]> = {
    [AccountBranchesColumn.LEGAL_ENTITY]: optionsData?.legalEntities ?? [],
    Status: [Status.ACTIVE, Status.INACTIVE],
  };

  const MENU_LIST = Object.keys(SUB_MENUS);

  const STATUS_VARIANT_MAP: StatusVariants = {
    Active: { badge: "success", dot: "#65A30D" },
  };

  const SUB_MENU_REGISTRY: Record<string, SubMenuConfig> = {
    [AccountBranchesColumn.LEGAL_ENTITY]: {
      type: "search",
      placeholder: "Search legal entity",
      isSingleSelect: true,
    },
    Status: { type: "status", isSingleSelect: true },
  };

  const columns: ColumnConfig<IBranchInfo>[] = [
    {
      key: "name",
      label: AccountBranchesColumn.BRANCH_NAME,
      visible: flags[AccountBranchesColumn.BRANCH_NAME],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),

      cellRenderer: (row, isLast) => {
        return hasPermission ? (
          <TableCellLink last={isLast} classes="w-[17%]">
            <Link
              to={PATHS.ADMIN.ACCOUNT_BRANCH(row.id || "")}
              data-testid={`account-branch-cell-id-${row.id}`}
            >
              {row.name}
            </Link>
          </TableCellLink>
        ) : (
          <TableCellText last={isLast}>{row.name}</TableCellText>
        );
      },
    },
    {
      key: "referenceNumber",
      label: AccountBranchesColumn.REFERENCE_NUMBER,
      visible: flags[AccountBranchesColumn.REFERENCE_NUMBER],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast}>{row.referenceNumber || ""}</TableCellText>
      ),
    },
    {
      key: "createdAt",
      label: AccountBranchesColumn.DATE_TIME,
      visible: flags[AccountBranchesColumn.DATE_TIME],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast}>
          {formatIsoDate(row?.createdAt) || "-"}
        </TableCellText>
      ),
    },
    {
      key: "legalEntityName",
      label: AccountBranchesColumn.LEGAL_ENTITY,
      visible: flags[AccountBranchesColumn.LEGAL_ENTITY],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast}>
          {row.legalEntityName || "-"}
        </TableCellText>
      ),
    },
    {
      key: "phone",
      label: AccountBranchesColumn.PHONE,
      visible: flags[AccountBranchesColumn.PHONE],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast}>{row.phone}</TableCellText>
      ),
    },
    {
      key: "active",
      label: AccountBranchesColumn.ACTIVITY,
      visible: flags[AccountBranchesColumn.ACTIVITY],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellBadge
          classes="w-[10%]"
          last={isLast}
          stage={row.active ? "success" : "default"}
        >
          {row.active ? Status.ACTIVE : Status.INACTIVE}
        </TableCellBadge>
      ),
    },
    {
      key: "address",
      label: AccountBranchesColumn.ADDRESS,
      visible: flags[AccountBranchesColumn.ADDRESS],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast} classes="w-[17%] break-all">
          {row.address}
        </TableCellText>
      ),
    },
  ];

  return (
    <>
      <FilterSearch
        menuList={MENU_LIST}
        subMenus={SUB_MENUS}
        subMenuRegistry={SUB_MENU_REGISTRY}
        statusVariant={STATUS_VARIANT_MAP}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        handleSetFilter={handleSetFilter}
      >
        <DropDownViewMenu
          viewList={TABLE_HEADERS}
          visibleColumns={visibleColumns}
          setVisibleColumns={setVisibleColumns}
        />
      </FilterSearch>

      {isBranchPending || isOptionPending ? (
        <Loader />
      ) : (
        <DynamicTable<IBranchInfo>
          data={branchData?.items || []}
          columns={columns}
          searchValue={searchValue}
          next={handleNext}
          prev={handlePrev}
          tableParams={branchData}
          setTableParams={setTableParams}
        />
      )}
    </>
  );
};

export default AccountBranchesTable;
